import { useMemo, useState } from "react";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useUIStore } from "@/stores/useUIStore";
import { useBillingSummary } from "@/api/billing";
import {
  plansProposes,
  type BillingPeriod,
  type PlanDescriptor,
} from "./plans";
import { PlanCardCompact } from "./PlanCardCompact";
import { ConfirmPlanChangeDialog } from "./ConfirmPlanChangeDialog";

/**
 * Mode 2 de BillingView : page dédiée pour changer de plan.
 *
 * Layout 2 colonnes : Configure (gauche, cycle) | Plans (droite, tabs Indé/Équipes).
 * Pas de card Enterprise ici — pour Enterprise, lien "Comparer tous les détails"
 * vers /pricing (nouvelle tab) qui contient Enterprise + CTA "Contactez-nous".
 *
 * Tabs disponibles : Indépendants (Solo+Team), Équipes (Business+Premium).
 * Default tab : Indé (per décision produit).
 */
export function ChangePlanView() {
  const { t } = useTranslation();
  const darkMode = useUIStore((s) => s.darkMode);
  const { locale = "fr" } = useParams<{ locale: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data } = useBillingSummary();
  const currentSlug = data?.summary?.plan_slug ?? "";
  const isLegacy = data?.summary?.is_legacy_plan ?? false;
  const currentInterval = data?.summary?.billing_interval;

  // Default billing : the cycle asked for (?cycle=, from "Switch to
  // monthly/yearly"), else the current one, else yearly.
  const cycleDemande = searchParams.get("cycle");
  const [billing, setBilling] = useState<BillingPeriod>(
    cycleDemande === "monthly" || cycleDemande === "yearly"
      ? cycleDemande
      : currentInterval === "month" ? "monthly" : "yearly",
  );
  const currentPeriod: BillingPeriod | null =
    currentInterval === "month" ? "monthly" : currentInterval === "year" ? "yearly" : null;
  const [confirmTarget, setConfirmTarget] = useState<PlanDescriptor | null>(
    null,
  );

  const plans = useMemo(() => plansProposes(), []);

  const handleBack = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("action");
      params.delete("cycle");
      return params;
    });
  };

  return (
    <>
      {/* Bandeau legacy migration (uniquement si user actuel est legacy) */}
      {isLegacy && (
        <div
          className={cn(
            "rounded-lg border p-4 mb-4",
            darkMode
              ? "border-blue-500/30 bg-blue-500/10 text-blue-200"
              : "border-blue-200 bg-blue-50 text-blue-900",
          )}
        >
          <p className="text-sm">
            {t(
              "manager.billing.legacyMigrationBanner",
              "Vous migrez depuis votre ancien plan. Choisissez votre nouveau forfait ci-dessous.",
            )}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("manager.billing.backToSubscription", "Mon abonnement")}
        </Button>
        <h2
          className={cn(
            "text-xl font-semibold",
            darkMode ? "text-white" : "text-slate-900",
          )}
        >
          {t("manager.billing.changePlanTitle", "Changer de plan")}
        </h2>
      </div>

      {/* Cycle au-dessus, plans en pleine largeur */}
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <p
            className={cn(
              "text-sm",
              darkMode ? "text-slate-400" : "text-slate-500",
            )}
          >
            {t("manager.billing.cycle", "Cycle")}
          </p>
          <div
            className={cn(
              "inline-flex rounded-md p-0.5",
              darkMode ? "bg-slate-800" : "bg-slate-100",
            )}
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={billing === "yearly"}
              onClick={() => setBilling("yearly")}
              className={cn(
                "text-xs font-medium px-3 py-1.5 rounded-sm transition-colors",
                billing === "yearly"
                  ? darkMode
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-white text-slate-900 shadow-sm"
                  : darkMode
                    ? "text-slate-400"
                    : "text-slate-600",
              )}
            >
              {t("manager.billing.yearlyShort", "Annuel -20%")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billing === "monthly"}
              onClick={() => setBilling("monthly")}
              className={cn(
                "text-xs font-medium px-3 py-1.5 rounded-sm transition-colors",
                billing === "monthly"
                  ? darkMode
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-white text-slate-900 shadow-sm"
                  : darkMode
                    ? "text-slate-400"
                    : "text-slate-600",
              )}
            >
              {t("manager.billing.monthly", "Mensuel")}
            </button>
          </div>
        </div>

        <div className="space-y-5">
          {/* Grille plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <PlanCardCompact
                key={plan.key}
                plan={plan}
                billing={billing}
                // Same plan on the other cycle is a change too (Solo yearly → Solo monthly).
                isCurrent={plan.key === currentSlug && billing === currentPeriod}
                onChoose={setConfirmTarget}
                darkMode={darkMode}
              />
            ))}
          </div>

          {/* Lien comparer */}
          <a
            href={`${import.meta.env.VITE_MARKETING_URL}/${locale}/pricing`}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center gap-1.5 text-sm font-medium hover:underline",
              darkMode ? "text-blue-400" : "text-blue-600",
            )}
          >
            {t(
              "manager.billing.compareAllDetails",
              "Comparer tous les détails",
            )}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      <ConfirmPlanChangeDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
        targetPlan={confirmTarget}
        billing={billing}
        isLegacyMigration={isLegacy}
      />
    </>
  );
}
